import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Save } from "lucide-react";
import { LivingSituationSelect } from "@/components/LivingSituationSelect";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function EditClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [physicians, setPhysicians] = useState([]);
  const [selectedPhysicianId, setSelectedPhysicianId] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    dob: "",
    ssn_last4: "",
    ssn: "",
    gender: "",
    living_situation: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    insurance_info: "",
    insurance_company: "",
    insurance_policy_number: "",
    medicaid_id: "",
    medicare_id: "",
    insurance_accepted: false,
    primary_diagnosis: "",
    secondary_diagnoses: "",
    care_reasons: "",
    is_diabetic: false,
    has_seizure_history: false,
    allergies: "",
    height: "",
    weight: "",
    dme_current: "",
    dme_needed: "",
    physician_id: "",
    physician_name: "",
    physician_clinic: "",
    physician_phone: "",
    physician_fax: "",
    physician_address: "",
    last_md_visit: "",
    has_specialist: false,
    specialist_name: "",
    has_previous_agency: false,
    previous_agency: "",
    preferred_pcw_name: "",
    preferred_pcw_phone: "",
    notes: "",
  });

  useEffect(() => {
    fetchClient();
    fetchPhysicians();
  }, [id]);

  const fetchPhysicians = async () => {
    try {
      const response = await axios.get(`${API}/physicians`);
      setPhysicians(response.data);
    } catch (error) {
      console.error("Failed to fetch physicians:", error);
    }
  };

  const handlePhysicianSelect = (physicianId) => {
    setSelectedPhysicianId(physicianId);
    if (physicianId === "manual") {
      // Clear fields for manual entry
      setFormData(prev => ({
        ...prev,
        physician_id: "",
        physician_name: "",
        physician_clinic: "",
        physician_phone: "",
        physician_fax: "",
        physician_address: "",
      }));
    } else if (physicianId) {
      const physician = physicians.find(p => p.id === physicianId);
      if (physician) {
        setFormData(prev => ({
          ...prev,
          physician_id: physician.id,
          physician_name: physician.name || "",
          physician_clinic: physician.clinic_name || "",
          physician_phone: physician.phone || "",
          physician_fax: physician.fax || "",
          physician_address: physician.address || "",
        }));
      }
    }
  };

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      const client = response.data;

      // Set selected physician if client has one
      if (client.physician_id) {
        setSelectedPhysicianId(client.physician_id);
      } else if (client.physician?.name) {
        // Has physician info but no ID - treat as manual entry
        setSelectedPhysicianId("manual");
      }

      setFormData({
        first_name: client.first_name || "",
        last_name: client.last_name || "",
        dob: client.dob || "",
        ssn_last4: client.ssn_last4 || "",
        ssn: "",
        gender: client.gender || "",
        living_situation: client.living_situation || "",
        phone: client.phone || "",
        email: client.email || "",
        address: client.address || "",
        city: client.city || "",
        state: client.state || "",
        zip_code: client.zip_code || "",
        emergency_contact_name: client.emergency_contact_name || "",
        emergency_contact_phone: client.emergency_contact_phone || "",
        emergency_contact_relation: client.emergency_contact_relation || "",
        insurance_info: client.insurance_info || "",
        insurance_company: client.insurance_company || "",
        insurance_policy_number: client.insurance_policy_number || "",
        medicaid_id: client.medicaid_id || "",
        medicare_id: client.medicare_id || "",
        insurance_accepted: client.insurance_accepted || false,
        primary_diagnosis: client.primary_diagnosis || "",
        secondary_diagnoses: client.secondary_diagnoses?.join(", ") || "",
        care_reasons: client.care_reasons || "",
        is_diabetic: client.is_diabetic || false,
        has_seizure_history: client.has_seizure_history || false,
        allergies: client.allergies || "",
        height: client.height || "",
        weight: client.weight || "",
        dme_current: client.dme_current || "",
        dme_needed: client.dme_needed || "",
        physician_id: client.physician_id || "",
        physician_name: client.physician?.name || "",
        physician_clinic: client.physician?.clinic_name || "",
        physician_phone: client.physician?.phone || "",
        physician_fax: client.physician?.fax || "",
        physician_address: client.physician?.address || "",
        last_md_visit: client.last_md_visit || "",
        has_specialist: client.has_specialist || false,
        specialist_name: client.specialist_name || "",
        has_previous_agency: client.has_previous_agency || false,
        previous_agency: client.previous_agency || "",
        preferred_pcw_name: client.preferred_pcw_name || "",
        preferred_pcw_phone: client.preferred_pcw_phone || "",
        notes: client.notes || "",
      });
    } catch (error) {
      console.error("Failed to fetch client:", error);
      toast.error("Failed to load client details");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name) {
      toast.error("Please provide first and last name");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        dob: formData.dob || null,
        ssn: (formData.ssn || "").replace(/\D/g, "") || null,
        gender: formData.gender || null,
        living_situation: formData.living_situation || null,
        phone: formData.phone || null,
        email: formData.email || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zip_code || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        emergency_contact_relation: formData.emergency_contact_relation || null,
        insurance_info: formData.insurance_info || null,
        insurance_company: formData.insurance_company || null,
        insurance_policy_number: formData.insurance_policy_number || null,
        medicaid_id: formData.medicaid_id || null,
        medicare_id: formData.medicare_id || null,
        insurance_accepted: formData.insurance_accepted,
        primary_diagnosis: formData.primary_diagnosis || null,
        secondary_diagnoses: formData.secondary_diagnoses
          ? formData.secondary_diagnoses.split(",").map((d) => d.trim())
          : [],
        care_reasons: formData.care_reasons || null,
        is_diabetic: formData.is_diabetic,
        has_seizure_history: formData.has_seizure_history,
        allergies: formData.allergies || null,
        height: formData.height || null,
        weight: formData.weight || null,
        dme_current: formData.dme_current || null,
        dme_needed: formData.dme_needed || null,
        physician_id: formData.physician_id || null,
        physician: {
          name: formData.physician_name || null,
          clinic_name: formData.physician_clinic || null,
          phone: formData.physician_phone || null,
          fax: formData.physician_fax || null,
          address: formData.physician_address || null,
        },
        last_md_visit: formData.last_md_visit || null,
        has_specialist: formData.has_specialist,
        specialist_name: formData.specialist_name || null,
        has_previous_agency: formData.has_previous_agency,
        previous_agency: formData.previous_agency || null,
        preferred_pcw_name: formData.preferred_pcw_name || null,
        preferred_pcw_phone: formData.preferred_pcw_phone || null,
        notes: formData.notes || null,
      };

      await axios.patch(`${API}/clients/${id}`, payload);
      toast.success("Client updated successfully!");
      navigate(`/clients/${id}`);
    } catch (error) {
      console.error("Failed to update client:", error);
      toast.error(error.response?.data?.detail || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/clients" className="hover:text-slate-700">
          Clients
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to={`/clients/${id}`} className="hover:text-slate-700">
          {formData.first_name} {formData.last_name}
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Edit</span>
      </div>

      {/* Back Button */}
      <Link
        to={`/clients/${id}`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Client
      </Link>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Edit Client</h1>
        <p className="page-subtitle">
          Update {formData.first_name} {formData.last_name}&apos;s information.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="form-section">
            <h3 className="form-section-title">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  data-testid="edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  data-testid="edit-last-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  data-testid="edit-dob"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select value={formData.gender || ""} onValueChange={(v) => handleSelectChange("gender", v)}>
                  <SelectTrigger data-testid="edit-gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="unknown">Unknown / not stated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ssn">Social Security Number</Label>
                <Input
                  id="ssn"
                  name="ssn"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder={formData.ssn_last4 ? `•••-••-${formData.ssn_last4} on file — type a new number to replace` : "123-45-6789"}
                  value={formData.ssn}
                  onChange={(e) => {
                    const d = e.target.value.replace(/\D/g, "").slice(0, 9);
                    const v = d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
                    setFormData((prev) => ({ ...prev, ssn: v }));
                  }}
                  data-testid="edit-ssn"
                />
                <p className="text-xs text-slate-500">Stored encrypted; only the last 4 digits are shown afterwards. Leave blank to keep the number on file.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="edit-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="patient@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="edit-email"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <h3 className="form-section-title">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  data-testid="edit-address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="edit-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) => handleSelectChange("state", value)}
                >
                  <SelectTrigger data-testid="edit-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  data-testid="edit-zip"
                />
              </div>
              <LivingSituationSelect value={formData.living_situation} onChange={(v) => handleSelectChange("living_situation", v)} testId="edit-living-situation" />
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="form-section">
            <h3 className="form-section-title">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_name">Name</Label>
                <Input
                  id="emergency_contact_name"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_phone">Phone</Label>
                <Input
                  id="emergency_contact_phone"
                  name="emergency_contact_phone"
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_relation">Relationship</Label>
                <Input
                  id="emergency_contact_relation"
                  name="emergency_contact_relation"
                  value={formData.emergency_contact_relation}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Insurance Information */}
          <div className="form-section">
            <h3 className="form-section-title">Insurance Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="insurance_company">Insurance Company</Label>
                <Input
                  id="insurance_company"
                  name="insurance_company"
                  placeholder="e.g., UnitedHealthcare, Aetna, Anthem BCBS"
                  value={formData.insurance_company}
                  onChange={handleChange}
                  data-testid="edit-insurance-company"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance_policy_number">Policy / Member Number</Label>
                <Input
                  id="insurance_policy_number"
                  name="insurance_policy_number"
                  placeholder="Policy or member ID"
                  value={formData.insurance_policy_number}
                  onChange={handleChange}
                  data-testid="edit-insurance-policy-number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="medicaid_id">Medicaid ID Number</Label>
                <Input
                  id="medicaid_id"
                  name="medicaid_id"
                  placeholder="Medicaid Recipient ID"
                  value={formData.medicaid_id}
                  onChange={handleChange}
                  data-testid="edit-medicaid-id"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="medicare_id">Medicare ID (MBI)</Label>
                <Input
                  id="medicare_id"
                  name="medicare_id"
                  placeholder="Medicare Beneficiary ID"
                  value={formData.medicare_id}
                  onChange={handleChange}
                  data-testid="edit-medicare-id"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="insurance_info">Additional Insurance Notes</Label>
                <Input
                  id="insurance_info"
                  name="insurance_info"
                  placeholder="Group #, secondary insurance, auth notes, etc."
                  value={formData.insurance_info}
                  onChange={handleChange}
                  data-testid="edit-insurance-info"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="insurance_accepted"
                  checked={formData.insurance_accepted}
                  onCheckedChange={(checked) =>
                    setFormData((p) => ({ ...p, insurance_accepted: checked }))
                  }
                  data-testid="edit-insurance-accepted"
                />
                <Label htmlFor="insurance_accepted">Insurance Accepted</Label>
              </div>
            </div>
          </div>

          {/* Medical Info */}
          <div className="form-section">
            <h3 className="form-section-title">Medical Information</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_diagnosis">Primary Diagnosis</Label>
                <Input
                  id="primary_diagnosis"
                  name="primary_diagnosis"
                  value={formData.primary_diagnosis}
                  onChange={handleChange}
                  data-testid="edit-diagnosis"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary_diagnoses">
                  Secondary Diagnoses (comma-separated)
                </Label>
                <Input
                  id="secondary_diagnoses"
                  name="secondary_diagnoses"
                  value={formData.secondary_diagnoses}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="care_reasons">Reason for Home Care</Label>
                <Textarea
                  id="care_reasons"
                  name="care_reasons"
                  rows={3}
                  value={formData.care_reasons}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Medical Conditions */}
            <div className="mt-4 space-y-4">
              <h4 className="text-sm font-medium text-slate-700">Medical Conditions</h4>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_diabetic"
                  checked={formData.is_diabetic}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("is_diabetic", checked)
                  }
                  data-testid="edit-diabetic"
                />
                <Label htmlFor="is_diabetic">
                  Client is Diabetic (triggers blood sugar monitoring alerts)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_seizure_history"
                  checked={formData.has_seizure_history}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("has_seizure_history", checked)
                  }
                  data-testid="edit-seizure"
                />
                <Label htmlFor="has_seizure_history">
                  Client has Seizure History (triggers seizure protocol alerts)
                </Label>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="allergies">Allergies</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setFormData((prev) => ({ ...prev, allergies: "NKDA (No Known Drug Allergies)" }))}
                    data-testid="edit-client-nkda-preset-btn"
                  >
                    NKDA
                  </Button>
                </div>
                <Textarea
                  id="allergies"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  placeholder="e.g., Penicillin, latex, peanuts (or NKDA if none)"
                  rows={2}
                  data-testid="edit-client-allergies-input"
                />
                <p className="text-xs text-slate-500">
                  Auto-fills into the CMS-485 and other clinical forms.
                </p>
              </div>
            </div>
          </div>

          {/* Physical Information & DME */}
          <div className="form-section">
            <h3 className="form-section-title">Physical Information &amp; DME Supplies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="height">Height</Label>
                <Input
                  id="height"
                  name="height"
                  placeholder={'e.g., 5\'6"'}
                  value={formData.height}
                  onChange={handleChange}
                  data-testid="edit-height"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight</Label>
                <Input
                  id="weight"
                  name="weight"
                  placeholder="e.g., 165 lbs"
                  value={formData.weight}
                  onChange={handleChange}
                  data-testid="edit-weight"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dme_current">Current DME (in use)</Label>
                <Textarea
                  id="dme_current"
                  name="dme_current"
                  rows={2}
                  placeholder="e.g., Walker, shower chair, hospital bed"
                  value={formData.dme_current}
                  onChange={handleChange}
                  data-testid="edit-dme-current"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dme_needed">DME Needed</Label>
                <Textarea
                  id="dme_needed"
                  name="dme_needed"
                  rows={2}
                  placeholder="e.g., Wheelchair, grab bars, incontinence supplies"
                  value={formData.dme_needed}
                  onChange={handleChange}
                  data-testid="edit-dme-needed"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Shows on the Face Sheet and pre-fills DME orders and the CMS-485.</p>
          </div>

          {/* Physician Info */}
          <div id="physician" className="form-section scroll-mt-4">
            <h3 className="form-section-title">Physician Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Select Physician</Label>
                <Select
                  value={selectedPhysicianId}
                  onValueChange={handlePhysicianSelect}
                >
                  <SelectTrigger data-testid="physician-select">
                    <SelectValue placeholder="Select a physician or enter manually" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">-- Enter Manually --</SelectItem>
                    {physicians.map((physician) => (
                      <SelectItem key={physician.id} value={physician.id}>
                        {physician.name} - {physician.clinic_name || "No clinic"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Select from registered physicians or choose &quot;Enter Manually&quot; to type in details
                </p>
              </div>
              
              {/* Show fields - read-only if physician selected from dropdown, editable if manual */}
              <div className="space-y-2">
                <Label htmlFor="physician_name">Physician Name</Label>
                <Input
                  id="physician_name"
                  name="physician_name"
                  value={formData.physician_name}
                  onChange={handleChange}
                  disabled={selectedPhysicianId && selectedPhysicianId !== "manual"}
                  className={selectedPhysicianId && selectedPhysicianId !== "manual" ? "bg-slate-50" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="physician_clinic">Clinic Name</Label>
                <Input
                  id="physician_clinic"
                  name="physician_clinic"
                  value={formData.physician_clinic}
                  onChange={handleChange}
                  disabled={selectedPhysicianId && selectedPhysicianId !== "manual"}
                  className={selectedPhysicianId && selectedPhysicianId !== "manual" ? "bg-slate-50" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="physician_phone">Phone</Label>
                <Input
                  id="physician_phone"
                  name="physician_phone"
                  type="tel"
                  value={formData.physician_phone}
                  onChange={handleChange}
                  disabled={selectedPhysicianId && selectedPhysicianId !== "manual"}
                  className={selectedPhysicianId && selectedPhysicianId !== "manual" ? "bg-slate-50" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="physician_fax">Fax</Label>
                <Input
                  id="physician_fax"
                  name="physician_fax"
                  type="tel"
                  value={formData.physician_fax}
                  onChange={handleChange}
                  disabled={selectedPhysicianId && selectedPhysicianId !== "manual"}
                  className={selectedPhysicianId && selectedPhysicianId !== "manual" ? "bg-slate-50" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_md_visit">Last MD Visit</Label>
                <Input
                  id="last_md_visit"
                  name="last_md_visit"
                  type="date"
                  value={formData.last_md_visit}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3 className="form-section-title">Additional Notes</h3>
            <div className="space-y-2">
              <Textarea
                id="notes"
                name="notes"
                rows={4}
                placeholder="Any additional notes about the client..."
                value={formData.notes}
                onChange={handleChange}
                data-testid="edit-notes"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-slate-200">
          <Link to={`/clients/${id}`}>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={saving} data-testid="save-client-btn">
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="spinner"></span>
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </Layout>
  );
}
